import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { apiFetch, useApiMutation, useApiQuery } from './client';
import type {
  CamadaLiquidacionListado,
  ConfiguracionLiquidacion,
  LiquidacionPreparacion,
  LiquidacionPreview,
  LiquidacionSolicitud,
} from '../liquidacion';
import type { Page } from '../types';
import type { EvolucionLiquidacion } from '../liquidacion-evolucion';

export function useLiquidacionEvolucion(id: number | null) {
  return useApiQuery<EvolucionLiquidacion>({
    queryKey: ['liquidacion-evolucion', id],
    queryFn: () => apiFetch(`/v1/camadas/${id}/liquidacion/evolucion`),
    enabled: id !== null,
    staleTime: 0,
  });
}

export function useCamadasLiquidacion(cerrada: boolean, year?: number, offset = 0) {
  const params = new URLSearchParams({
    cerrada: String(cerrada),
    limit: cerrada ? '25' : '1',
    offset: String(offset),
  });
  if (year !== undefined) params.set('year', String(year));
  return useApiQuery<Page<CamadaLiquidacionListado>>({
    queryKey: ['camadas', 'liquidaciones', cerrada, year ?? 'all', offset],
    queryFn: () => apiFetch(`/v1/camadas?${params}`),
    staleTime: 0,
  });
}

export function useLiquidacion(id: number | null) {
  return useApiQuery<LiquidacionPreparacion>({
    queryKey: ['liquidacion', id],
    queryFn: () => apiFetch(`/v1/camadas/${id}/liquidacion`),
    enabled: id !== null,
    staleTime: 0,
  });
}
export function useLiquidacionPreview(id: number, solicitud: LiquidacionSolicitud | null) {
  const ownerId = useAuthStore((s) => s.user?.id ?? null);
  return useQuery({
    queryKey: ['liquidacion-preview', id, solicitud, 'owner', ownerId],
    networkMode: 'always',
    queryFn: ({ signal }) =>
      apiFetch<LiquidacionPreview>(`/v1/camadas/${id}/liquidacion/preview`, {
        method: 'POST',
        body: JSON.stringify(solicitud),
        signal,
      }),
    enabled: solicitud !== null,
    retry: false,
    staleTime: 0,
  });
}
export function useGuardarLiquidacion(id: number) {
  return useApiMutation<LiquidacionSolicitud, LiquidacionPreparacion>({
    mutationFn: (body, context) =>
      apiFetch(`/v1/camadas/${id}/liquidacion`, {
        offlineBase: context.offlineBase,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [
      ['liquidacion', id],
      ['camadas', id, 'detalle'],
      ['camadas', 'liquidaciones'],
    ],
  });
}
export function useConfiguracionLiquidacion() {
  return useApiQuery<ConfiguracionLiquidacion>({
    queryKey: ['catalogos', 'liquidacion'],
    queryFn: () => apiFetch('/v1/catalogos/liquidacion'),
    staleTime: 0,
  });
}
export function useGuardarConfiguracionLiquidacion() {
  return useApiMutation<ConfiguracionLiquidacion, ConfiguracionLiquidacion>({
    mutationFn: (body, context) =>
      apiFetch('/v1/catalogos/liquidacion', {
        offlineBase: context.offlineBase,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [
      ['catalogos', 'liquidacion'],
      ['liquidacion'],
      ['liquidacion-preview'],
      ['liquidacion-evolucion'],
    ],
  });
}
